/**
 * Service Zone Validation
 * 
 * Defines coverage polygons (FRZ, SRZ-GTA, SRZ-Kitchener) and provides
 * point-in-polygon validation to determine if a given lat/lng is within
 * the service coverage area.
 */

export type ServiceZone = "FRZ" | "SRZ" | "OUT_OF_COVERAGE";

interface GeoJSONPolygon {
  type: "Polygon";
  coordinates: number[][][]; // [[[lon, lat], [lon, lat], ...]]
}

interface GeoJSONFeature {
  type: "Feature";
  geometry: GeoJSONPolygon;
  properties: { name: string };
}

/**
 * Ray Casting Algorithm for Point-in-Polygon Test
 * 
 * Projects a horizontal ray from the point to the right and counts intersections
 * with polygon edges. Odd count = inside, even count = outside.
 * 
 * @param point [lng, lat]
 * @param polygon Array of [lng, lat] coordinates forming the polygon
 * @returns true if point is inside polygon
 */
function isPointInPolygon(point: number[], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  // Iterate through each edge of the polygon
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    // Check if ray from point intersects with edge from (xj, yj) to (xi, yi)
    const intersect =
      yi > y !== yj > y && // Edge crosses horizontal line through point
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi; // Intersection is to the right

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Coverage polygons in GeoJSON format
 * Coordinates are in [longitude, latitude] order
 */
const COVERAGE_ZONES: GeoJSONFeature[] = [
  {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [[
        [-79.455, 43.632], [-79.44, 43.629], [-79.425, 43.628], [-79.41, 43.629],
        [-79.395, 43.629], [-79.38, 43.6385], [-79.36, 43.6405], [-79.352, 43.6485],
        [-79.354, 43.66], [-79.3625, 43.668], [-79.376, 43.671], [-79.392, 43.67],
        [-79.409, 43.668], [-79.424, 43.665], [-79.441, 43.658], [-79.452, 43.647],
        [-79.455, 43.64], [-79.455, 43.632]
      ]]
    },
    properties: { name: "FRZ" }
  },
  {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [[
        [-79.7368774, 43.3801366], [-79.4839403, 43.6042608], [-79.299571, 43.6055405],
        [-79.0223113, 43.8200331], [-78.8700782, 43.8477739], [-78.7838939, 43.8681989],
        [-78.6724719, 43.8801426], [-78.5902338, 43.8891924], [-78.5205847, 43.9885534],
        [-78.6322861, 44.1037184], [-79.0638901, 44.0425122], [-79.1379339, 44.1137137],
        [-79.2201097, 44.2512068], [-79.3202991, 44.289779], [-79.4732199, 44.2929933],
        [-79.5364967, 44.2576364], [-79.5839553, 44.1708504], [-79.5839556, 44.0776364],
        [-79.4837664, 43.9426363], [-79.4837668, 43.8108503], [-79.7579696, 43.7787076],
        [-79.8660115, 43.729685], [-79.7573986, 43.5963543], [-79.8693481, 43.4876038],
        [-80.2274738, 43.1528826], [-79.7014207, 43.0144196], [-79.3204767, 42.9761657],
        [-79.1476589, 43.1561139], [-79.7698409, 43.2972247], [-79.7368774, 43.3801366]
      ]]
    },
    properties: { name: "SRZ-GTA" }
  },
  {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [[
        [-80.5391423, 43.5833276], [-80.6750981, 43.5246073], [-80.6778447, 43.4817751],
        [-80.6338994, 43.3401134], [-80.4251591, 43.3411122], [-80.3056828, 43.4349238],
        [-80.353748, 43.5325727], [-80.4759709, 43.5863118], [-80.5391423, 43.5833276]
      ]]
    },
    properties: { name: "SRZ-Kitchener" }
  }
];

/**
 * Determines the service zone for a given latitude/longitude point
 * 
 * Priority order: FRZ > SRZ (any) > OUT_OF_COVERAGE
 * 
 * @param lat Latitude of the point
 * @param lng Longitude of the point
 * @returns "FRZ" | "SRZ" | "OUT_OF_COVERAGE"
 */
export function getServiceZone(lat: number, lng: number): ServiceZone {
  const point = [lng, lat]; // Convert to [longitude, latitude] format

  // Check FRZ first (highest priority)
  const frzPolygon = COVERAGE_ZONES[0];
  if (isPointInPolygon(point, frzPolygon.geometry.coordinates[0])) {
    return "FRZ";
  }

  // Check all SRZ polygons (GTA and Kitchener)
  for (let i = 1; i < COVERAGE_ZONES.length; i++) {
    const polygon = COVERAGE_ZONES[i];
    if (isPointInPolygon(point, polygon.geometry.coordinates[0])) {
      return "SRZ";
    }
  }

  // Point is not in any coverage zone
  return "OUT_OF_COVERAGE";
}

/**
 * Example usage and validation tests
 */
if (import.meta.env.DEV) {
  // Test cases for validation
  console.log("Service Zone Validation Tests:");
  
  // Downtown Toronto (inside FRZ)
  console.log("Downtown Toronto:", getServiceZone(43.653226, -79.383184)); // Expected: FRZ
  
  // GTA area (inside SRZ-GTA)
  console.log("GTA area:", getServiceZone(43.856633, -79.343126)); // Expected: SRZ
  
  // Kitchener area (inside SRZ-Kitchener)
  console.log("Kitchener area:", getServiceZone(43.451639, -80.492533)); // Expected: SRZ
  
  // Outside coverage (e.g., Vancouver)
  console.log("Vancouver:", getServiceZone(49.282729, -123.120738)); // Expected: OUT_OF_COVERAGE
}

